import type { Customer, Lead } from './types'

// Pure helpers for lead → customer conversion. No DOM here, so
// scripts/lead-conversion.test.mjs can test it headlessly. Two rules must
// hold word-for-word against the server copy in functions/lib/convert-lead.ts:
//
//   1. cycleDaysFromWeeks — the day proposal the user reviews must be the day
//      count the server stores, or the review approves a lie.
//   2. the draft mapping below mirrors customerDraftFromLead field-for-field.
//
// Conversion itself is always EXPLICIT (a confirmed lead + a button + a
// choice); nothing in here runs off a status change.

// Fractional weeks become days by rounding half-up, minimum 1 day; a lead
// with no cycle stays cycle-less (no invented cycle).
export function cycleDaysFromWeeks(weeks: number | null | undefined): number | null {
  if (weeks === null || weeks === undefined || !Number.isFinite(weeks) || weeks <= 0) return null
  return Math.max(1, Math.round(weeks * 7))
}

// The review line for the conversion panel. Fractional cycles are spelled
// out as a proposal — "2.5 weeks → 18 days (rounded)" — so the rounding is
// agreed, not implied.
export function cycleProposalText(weeks: number | null | undefined): string {
  if (weeks === null || weeks === undefined || !Number.isFinite(weeks) || weeks <= 0) return 'No cycle'
  const days = cycleDaysFromWeeks(weeks)
  return Number.isInteger(weeks * 7)
    ? `${weeks} weeks → ${days} days`
    : `${weeks} weeks → ${days} days (rounded)`
}

// Only a lead whose derived status is CONFIRMED and that carries no live
// customer link can be converted. (A link to a deleted customer reads as
// unconverted here; the server still refuses to re-convert it — that link is
// history, and its error explains the recovery.)
export function canConvertLead(lead: Pick<Lead, 'status' | 'live_customer_id'>): boolean {
  return lead.status === 'confirmed' && !lead.live_customer_id
}

// The request body for POST /api/leads/:id/convert. The lead version is the
// one on screen: a duplicate click under a second key arrives stale and the
// server's guarded update refuses to convert twice.
// A new customer may carry the postcode the owner checked in the window (bug hunt 8 Oct 2026); enquiries have none.
export type ConvertChoice = { mode: 'create'; postcode?: string | null } | { mode: 'existing'; customer: Pick<Customer, 'id' | 'version'> }

export function convertBody(lead: Pick<Lead, 'version'>, choice: ConvertChoice): Record<string, unknown> {
  return choice.mode === 'create'
    ? { mode: 'create', version: lead.version, ...(choice.postcode?.trim() ? { postcode: choice.postcode.trim() } : {}) }
    : {
        mode: 'existing',
        version: lead.version,
        customer_id: choice.customer.id,
        customer_version: choice.customer.version,
      }
}

// A UK postcode at the end of an enquiry's address ("1 Beech Road E17 9ZZ" gives "E17 9ZZ"), offered in the window
// for the owner to check; null when there is none. A US round reads a ZIP ("12 Elm St, Austin, TX 78701" gives
// "78701"), a Canadian one a postal code ("M5V 3L9"; Step 2 round 3). Never saved unchecked.
export function postcodeAtEnd(address: string | null, country = 'GB'): string | null {
  if (!address) return null
  if (country === 'US') return /(?:^|[\s,])(\d{5}(?:-\d{4})?)\s*$/.exec(address)?.[1] ?? null
  if (country === 'CA') {
    const ca = /(?:^|[\s,])([A-Z]\d[A-Z])\s*(\d[A-Z]\d)\s*$/i.exec(address)
    return ca ? `${ca[1]} ${ca[2]}`.toUpperCase() : null
  }
  if (country !== 'GB') return null
  const m = /(?:^|[\s,])([A-Z]{1,2}\d[A-Z\d]?)\s*(\d[A-Z]{2})\s*$/i.exec(address)
  return m ? `${m[1]} ${m[2]}`.toUpperCase() : null
}

// The address without its postcode, when it ends with exactly the postcode given (any case, any spacing), with the
// comma or spaces before it; any other address is kept as typed. Word for word the server's copy in
// functions/lib/convert-lead.ts, so the address the owner reviews is the address saved.
export function addressWithoutPostcode(address: string | null, postcode: string | null | undefined): string | null {
  if (!address || !postcode) return address
  const want = postcode.replace(/\s+/g, '').toUpperCase()
  if (!want) return address
  let seen = ''
  for (let i = address.length - 1; i >= 0; i--) {
    if (/\s/.test(address[i])) continue
    seen = address[i].toUpperCase() + seen
    if (seen.length === want.length) {
      // A whole word only: "ZZ" is not the postcode at the end of "E17 2ZZ".
      if (seen !== want || i > 0 && !/[\s,]/.test(address[i - 1])) return address
      const rest = address.slice(0, i).replace(/[\s,]+$/, '')
      return rest || address
    }
  }
  return address
}

// The values a created customer will carry, for the read-only review in the
// conversion panel — the same mapping the server applies (origin comes from
// the lead's "initial lead" column; the first clean price is the lead's
// initial price). Editing happens upstream on the lead itself, never here.
export interface ConversionDraft {
  name: string
  address: string | null
  phone: string | null
  origin: string | null
  notes: string | null
  first_clean_price_pence: number | null
  reg_price_pence: number | null
  cycle_days: number | null
  cycle_rounded: boolean
}

export function conversionDraft(lead: Lead): ConversionDraft {
  return {
    name: lead.name,
    address: lead.address,
    phone: lead.phone,
    origin: lead.initial_lead,
    notes: lead.notes,
    first_clean_price_pence: lead.initial_pence,
    reg_price_pence: lead.regular_pence,
    cycle_days: cycleDaysFromWeeks(lead.cycle_weeks),
    cycle_rounded: lead.cycle_weeks !== null && !Number.isInteger(lead.cycle_weeks * 7),
  }
}

// ---- possible matches ---------------------------------------------------------
//
// Candidates only — NEVER an auto-link. A lead may legitimately name-match an
// existing customer and still deserve a fresh row; the person converting
// decides. Matching is deliberately generous (fuzzy enough to surface
// "Smith" for "Smith & Sons", either way round) because a missed duplicate
// is worse than an extra suggestion.

export type MatchReason = 'name' | 'phone' | 'address'

export interface CustomerMatch {
  customer: Customer
  reasons: MatchReason[]
}

const norm = (v: string | null | undefined): string => (v ?? '').trim().toLowerCase().replace(/\s+/g, ' ')
const digit = (v: string | null | undefined): string => (v ?? '').replace(/\D/g, '')

const eitherContains = (a: string, b: string): boolean => a !== '' && b !== '' && (a.includes(b) || b.includes(a))

export function findCustomerMatches(
  lead: Pick<Lead, 'name' | 'address' | 'phone'>,
  customers: readonly Customer[],
  limit = 5,
): CustomerMatch[] {
  const name = norm(lead.name)
  const tel = digit(lead.phone)
  const addr = norm(lead.address)
  const out: CustomerMatch[] = []
  for (const c of customers) {
    const reasons: MatchReason[] = []
    if (eitherContains(norm(c.name), name)) reasons.push('name')
    // Short digit runs ("no", "1") collide constantly — a phone match needs a
    // number worth dialling on BOTH sides.
    if (tel.length >= 7 && digit(c.phone) === tel) reasons.push('phone')
    if (eitherContains(norm(c.address), addr)) reasons.push('address')
    if (reasons.length > 0) out.push({ customer: c, reasons })
  }
  // Strongest first (most reasons), then by name so the list is stable.
  out.sort(
    (a, b) =>
      b.reasons.length - a.reasons.length || a.customer.name.localeCompare(b.customer.name),
  )
  return out.slice(0, limit)
}
